import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/state/AuthStore'
import { ref } from 'vue'

// Lander
const LandingPage = () => import('@/pages/lander/LandingPage.vue')
const FeaturesView = () => import('@/pages/lander/FeaturesView.vue')
const PricingView = () => import('@/pages/lander/PricingView.vue')
const BookDemoView = () => import('@/pages/lander/BookDemoView.vue')

// Auth
const LoginPage = () => import('@/pages/auth/LoginPage.vue')
const ForgotPassword = () => import('@/pages/auth/ForgotPassword.vue')
// Core
const PipelineView = () => import('@/pages/core/PipelineView.vue')
const LeadsPage = () => import('@/pages/core/LeadsPage.vue')
const LeadDetail = () => import('@/pages/core/LeadDetail.vue')
const DealDetail = () => import('@/pages/core/DealDetail.vue')
const AccountPage = () => import('@/pages/core/AccountPage.vue')
// Admin
const FundersPage = () => import('@/pages/admin/FundersPage.vue')
const ImportLeads = () => import('@/pages/admin/ImportLeads.vue')
const ManageUsers = () => import('@/pages/admin/ManageUsers.vue')
const UserDetail = () => import('@/pages/admin/UserDetail.vue')
const FundingReport = () => import('@/pages/admin/FundingReport.vue')
const AdminSettings = () => import('@/pages/admin/AdminSettings.vue')
// Mobile
const MobileLogin = () => import('@/pages/mobile/MobileLogin.vue')
const MobileManageUsers = () => import('@/pages/mobile/MobileManageUsers.vue')
const MobileUserDetail = () => import('@/pages/mobile/MobileUserDetail.vue')
const MobileLeadDetail = () => import('@/pages/mobile/MobileLeadDetail.vue')
const MobileLeadsPage = () => import('@/pages/mobile/MobileLeadsPage.vue')
const MobileAdminSettings = () => import('@/pages/mobile/MobileAdminSettings.vue')
const MobilePipeline = () => import('@/pages/mobile/MobilePipeline.vue')
const MobileDealDetail = () => import('@/pages/mobile/MobileDealDetail.vue')
// Other
const TestBench = () => import('@/pages/TestBench.vue')
const NotFound404 = () => import('@/pages/core/404Page.vue')

function isMobile() {
  return window.innerWidth <= 768
}

export const loadingRoute = ref(false)

const routes = [
  {
    path: '/',
    redirect: '/lander',
    meta: { allowUnauthenticated: true }
  },
  {
    path: '/lander',
    meta: { allowUnauthenticated: true },
    children: [
      {
        path: '',
        name: 'landing-page',
        component: LandingPage,
        meta: { allowUnauthenticated: true }
      },
      {
        path: 'features',
        name: 'features',
        component: FeaturesView,
        meta: { allowUnauthenticated: true }
      },
      {
        path: 'pricing',
        name: 'pricing',
        component: PricingView,
        meta: { allowUnauthenticated: true }
      },
      {
        path: 'demo',
        name: 'book-demo',
        component: BookDemoView,
        meta: { allowUnauthenticated: true }
      }
    ]
  },
  {
    path: '/login',
    name: 'login',
    component: isMobile() ? MobileLogin : LoginPage,
    meta: { allowUnauthenticated: true }
  },
  {
    path: '/forgot',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { allowUnauthenticated: true }
  },
  {
    path: '/account',
    name: 'AccountPage',
    component: AccountPage
  },

  {
    path: '/leads/:id',
    name: 'LeadDetail',
    component: isMobile() ? MobileLeadDetail : LeadDetail
  },
  {
    path: '/leads',
    name: 'LeadsPage',
    component: isMobile() ? MobileLeadsPage : LeadsPage
  },
  {
    path: '/pipeline',
    name: 'PipelineView',
    component: isMobile() ? MobilePipeline : PipelineView
  },
  {
    path: '/deals/:id',
    name: 'DealDetail',
    component: isMobile() ? MobileDealDetail : DealDetail
  },
  {
    path: '/admin',
    meta: { requiresAdmin: true },
    children: [
      {
        path: 'settings',
        name: 'AdminSettings',
        component: isMobile() ? MobileAdminSettings : AdminSettings
      },
      {
        path: 'funders',
        name: 'FundersPage',
        component: FundersPage
      },
      {
        path: 'import',
        name: 'ImportLeads',
        component: ImportLeads
      },
      {
        path: 'users/:id',
        name: 'UserDetail',
        component: isMobile() ? MobileUserDetail : UserDetail
      },
      {
        path: 'users',
        name: 'ManageUsers',
        component: isMobile() ? MobileManageUsers : ManageUsers
      },
      {
        path: 'report',
        name: 'ReportsPage',
        component: FundingReport
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound404,
    meta: { allowUnauthenticated: true }
  },
  {
    path: '/test',
    name: 'TestBench',
    component: TestBench,
    meta: { allowUnauthenticated: true }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  loadingRoute.value = true

  const isPublic = to.matched.some((r) => r.meta.allowUnauthenticated)
  const needsAdmin = to.matched.some((r) => r.meta.requiresAdmin)

  if (isPublic) return next()

  const authStore = useAuthStore()
  if (!authStore.isAuthenticated && authStore.refresh) {
    try {
      await authStore.loadTokens()
    } catch (err) {
      console.error('Error loading tokens:', err)
      authStore.logout()
    }
  }

  if (needsAdmin) {
    if (!authStore.isAuthenticated) {
      return next({
        name: 'login',
        query: { redirect: to.fullPath }
      })
    }
    if (authStore.isAdmin) {
      return next()
    } else {
      return next({ name: 'LeadsPage' }) // Redirect non-admin users
    }
  }

  if (authStore.isAuthenticated) {
    if (to.name === 'DealDetail' && authStore.isJuniorRep) {
      return next({ name: 'PipelineView' })
    }
    return next()
  }

  return next({ name: 'login', query: { redirect: to.fullPath } })
})

router.afterEach(() => {
  loadingRoute.value = false
})

export default router
